import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const HTMLCodeQuality = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Best Practices</div>
                <h1>HTML Code Quality & Best Practices</h1>
                <p className="hero-lead">
                    Write clean, maintainable, and professional HTML code. Learn industry best practices,
                    coding conventions, and techniques for creating high-quality web pages.
                </p>
            </div>

            <div className="content-section">
                <h2>1. The Hidden Cost: Technical Debt in HTML</h2>
                <p>
                    Technical debt in HTML is often overlooked because "browsers are forgiving."
                    A browser will try its best to render even the most malformed markup, but
                    this forgiveness comes at a steep price: <strong>Performance Overhead</strong>.
                    When a browser encounters invalid nesting or deprecated tags, it must
                    initiate its "Error Recovery" algorithms, which block the main thread and
                    delay the First Contentful Paint (FCP).
                </p>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>The Reflow Tax</h3>
                        <p>Poorly structured HTML forces the browser's layout engine to
                            re-calculate the entire <strong>Box Model</strong> tree frequently.
                            Using semantic elements like <code>&lt;main&gt;</code> and
                            <code>&lt;section&gt;</code> helps the browser create a more
                            efficient internal representation of the page.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>2. Semantic SEO: How Google 'Sees' Your Quality</h2>
                <p>
                    Search engines like Google don't just "read" your textthey use the
                    <strong>DOM Structure</strong> to determine the information hierarchy
                    of your page. Quality HTML acts as a roadmap for web crawlers.
                    A page that uses nested <code>&lt;div&gt;</code> tags instead of
                    a clear <code>&lt;article&gt;</code> and <code>&lt;h1&gt;-&lt;h6&gt;</code>
                    hierarchy signals to search engines that the content is low-value or
                    unorganized.
                </p>
                <ProTip><p><strong>SEO Insight:</strong> Heading levels aren't for font size;
                        they're for document structure. Skipping heading levels (e.g.,
                        going from <code>&lt;h2&gt;</code> to <code>&lt;h4&gt;</code>)
                        is a major code quality violation that impacts your E-E-A-T
                        (Experience, Expertise, Authoritativeness, and Trustworthiness)
                        signals.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>3. The Accessibility Object Model (AOM)</h2>
                <p>
                    Beyond the DOM, high-quality HTML also generates the
                    <strong>Accessibility Tree</strong> or Accessibility Object Model.
                    This is what Screen Readers actually interact with. If you use a
                    <code>&lt;div&gt;</code> as a button, it won't appear as an interactive
                    element in the AOM, rendering your site unusable for a significant
                    portion of users.
                </p>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>Semantic Parity</h3>
                        <p>High-quality code ensures "Semantic Parity," where the
                            visual representation matches the programmatic meaning.
                            A clickable "Learn More" link should be an <code>&lt;a&gt;</code> tag,
                            not a <code>&lt;span&gt;</code> with a JavaScript listener.</p>
                    </div>
                    <div className="concept-card">
                        <h3>Focus Management</h3>
                        <p>Quality HTML handles the <strong>Tab Order</strong> naturally.
                            Manual <code>tabindex</code> manipulation is a sign of
                            poor code quality and often leads to "Keyboard Traps"
                            where users cannot escape a specific section of your page.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>2. Modern Architecture: Atomic Design in HTML</h2>
                <p>
                    Quality HTML is not just about tags; it's about <strong>Architecture</strong>.
                    Modern web development often follows the <strong>Atomic Design</strong>
                    methodology, where the DOM is broken down into five distinct levels:
                </p>
                <ul>
                    <li><strong>Atoms:</strong> Basic building blocks like buttons, inputs, or headers.</li>
                    <li><strong>Molecules:</strong> Groups of atoms functioning together (e.g., a search form).</li>
                    <li><strong>Organisms:</strong> Complex components composed of molecules (e.g., a navigation bar).</li>
                    <li><strong>Templates:</strong> Page-level objects that place organisms into a layout.</li>
                    <li><strong>Pages:</strong> Specific instances of templates with real content.</li>
                </ul>
                <ProTip><p><strong>Senior Engineer Tip:</strong> Treat your HTML as a
                        <strong>Living Documentation</strong>. Use data attributes
                        (<code>data-component="header"</code>) to decouple your
                        markup from your styling, allowing for a more robust
                        component-based architecture.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>Document Structure</h2>

                <h3>Good Document Structure</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="description" content="Page description">
    <title>Page Title</title>
    
    <!-- CSS --&gt;
    <link rel="stylesheet" href="styles.css">
    
    <!-- Favicon --&gt;
    <link rel="icon" type="image/png" href="/favicon.png">
</head>
<body>
    <!-- Page content --&gt;
    <header>
        <nav><!-- Navigation --&gt;</nav>
    </header>
    
    <main>
        <!-- Main content --&gt;
    </main>
    
    <footer>
        <!-- Footer content --&gt;
    </footer>
    
    <!-- JavaScript at end of body --&gt;
    <script src="script.js"></script>
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>4. Advanced Performance: The Critical Request Chain</h2>
                <p>
                    Writing quality HTML is a performance optimization in itself. The browser
                    processes HTML incrementally. As it parses the document, it identifies
                    external resources (CSS, JS, Fonts) and adds them to the
                    <strong>Critical Request Chain</strong>.
                </p>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>Resource Hinting</h3>
                        <p>Use <code>&lt;link rel="preload"&gt;</code> for essential assets
                            like hero images and fonts. Use <code>&lt;link rel="preconnect"&gt;</code>
                            for third-party origins like Google Fonts or CDNs to reduce
                            DNS/TCP connection latency.</p>
                    </div>
                    <div className="concept-card">
                        <h3>DOM Size & Depth</h3>
                        <p>Google's Lighthouse audit flags pages with more than 1,500
                            nodes or a depth greater than 32. Deeply nested HTML
                            increases memory usage and makes "Style Calculations"
                            linearly slower on mobile devices.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>5. Security: The Front-Line Defense</h2>
                <p>
                    HTML is the primary vector for <strong>Cross-Site Scripting (XSS)</strong> attacks.
                    Quality markup includes security headers and attributes that
                    harden the application against malicious injections.
                </p>
                <ProTip><p><strong>Security Standard:</strong> Always use <code>rel="noopener noreferrer"</code>
                        on all external links (<code>target="_blank"</code>). This prevents the
                        destination page from accessing your site's <code>window.opener</code> object,
                        thwarting a common phishing vector known as "Tabnabbing."</p></ProTip>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>Subresource Integrity (SRI)</h3>
                        <p>When loading scripts from CDNs, use the <code>integrity</code>
                            attribute. This ensures that if the CDN is compromised and
                            the script is altered, the browser will refuse to execute it.</p>
                    </div>
                    <div className="concept-card">
                        <h3>Content Security Policy (CSP)</h3>
                        <p>While often handled at the server level, CSP can be defined
                            via a <code>&lt;meta&gt;</code> tag. It restricts where
                            scripts and styles can be loaded from, effectively
                            killing most XSS vulnerabilities at the source.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>6. Semantic Comparison</h2>
                <h3> Non-Semantic</h3>
                <div className="code-example error">
                    <CodeBlock language="html" code={`<div class="header">
    <div class="nav">
        <div class="nav-item"><a href="/">Home</a></div>
        <div class="nav-item"><a href="/about">About</a></div>
    </div>
</div>

<div class="content">
    <div class="article">
        <div class="article-title">Title</div>
        <div class="article-body">Content...</div>
    </div>
</div>

<div class="footer">Footer</div>`} />
                </div>

                <h3>Semantic</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<header>
    <nav>
        <ul>
            <li><a href="/">Home</a></li>
            <li><a href="/about">About</a></li>
        </ul>
    </nav>
</header>

<main>
    <article>
        <h1>Title</h1>
        <p>Content...</p>
    </article>
</main>

<footer>Footer</footer>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Indentation & Formatting</h2>

                <h3>Consistent Indentation</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Use 2 or 4 spaces (be consistent) --&gt;
<div class="container">
    <header>
        <h1>Title</h1>
        <nav>
            <ul>
                <li><a href="/">Home</a></li>
                <li><a href="/about">About</a></li>
            </ul>
        </nav>
    </header>
</div>`} />
                </div>

                <h3> Poor Formatting</h3>
                <div className="code-example error">
                    <CodeBlock language="html" code={`<div class="container"><header><h1>Title</h1>
<nav><ul><li><a href="/">Home</a></li><li><a href="/about">About</a></li>
</ul></nav></header></div>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>7. Maintainability: CSS Methodologies in HTML</h2>
                <p>
                    Your HTML structure determines how you write your CSS. High-quality
                    code uses established naming conventions to keep the global
                    namespace clean and prevent selector collisions.
                </p>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>BEM (Block Element Modifier)</h3>
                        <p>A strictly semantic naming convention. <code>.card</code> is the block,
                            <code>.card__title</code> is the element, and <code>.card--featured</code>
                            is the modifier. This makes the relationship between components
                            obvious from the HTML alone.</p>
                    </div>
                    <div className="concept-card">
                        <h3>Utility-First (Tailwind style)</h3>
                        <p>Focuses on composing small, atomic classes (<code>flex pt-4 text-center</code>).
                            While it results in "noisy" HTML, it guarantees that your
                            CSS bundle size stays flat as your project grows.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>8. Future-Proofing: Web Components & Custom Elements</h2>
                <p>
                    The ultimate evolution of HTML code quality is the <strong>Web Component</strong>.
                    Standardized by the W3C, Web Components allow you to create reusable,
                    encapsulated custom tags that work across all modern browsers and
                    frameworks.
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>The Modern Custom Element Boilerplate</span>
                    </div>
                    <CodeBlock language="javascript" code={`class UserBadge extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' }); // Encapsulated styles
        this.shadowRoot.innerHTML = \`
            <style> :host {'{'} color: blue; {'}'} </style>
            <div>User: <slot></slot></div>
        \`;
    }
}
customElements.define('user-badge', UserBadge);`} />
                </div>
                <ProTip><p><strong>Design System Insight:</strong> Using custom elements
                        (e.g., <code>&lt;ff-button&gt;</code>) ensures that your core
                        UI patterns are protected from global CSS leaks and can be
                        easily updated in one place across massive applications.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>10. Naming Conventions</h2>
                <h3>Class Names</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Good: Descriptive, lowercase, hyphens --&gt;
<div class="user-profile">
    <img class="profile-avatar" src="avatar.jpg" alt="User avatar">
    <h2 class="profile-name">John Doe</h2>
    <p class="profile-bio">Software developer</p>
</div>

<!--  Bad: Unclear, mixed case, underscores --&gt;
<div class="UP">
    <img class="Prof_Av" src="avatar.jpg">
    <h2 class="pName">John Doe</h2>
    <p class="pb">Software developer</p>
</div>`} />
                </div>

                <h3>ID Names</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Good: Unique, descriptive --&gt;
<header id="main-header">
<nav id="primary-navigation">
<form id="contact-form">

<!--  Bad: Generic, unclear --&gt;
<header id="h1">
<nav id="nav1">
<form id="f">`} />
                </div>
            </div >

            <div className="content-section">
                <h2>Comments</h2>

                <h3>Useful Comments</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Main Navigation --&gt;
<nav class="main-nav">
    <!-- ... --&gt;
</nav>

<!-- Hero Section --&gt;
<section class="hero">
    <!-- ... --&gt;
</section>

<!-- Product Grid --&gt;
<div class="product-grid">
    <!-- Individual product cards --&gt;
</div>

<!-- End of Main Content --&gt;

<!-- Footer --&gt;
<footer>
    <!-- Contact information --&gt;
    <!-- Social media links --&gt;
    <!-- Copyright notice --&gt;
</footer>`} />
                </div>

                <h3> Useless Comments</h3>
                <div className="code-example error">
                    <CodeBlock language="html" code={`<!-- This is a div --&gt;
<div>
    <!-- This is a paragraph --&gt;
    <p>Text</p>
</div>

<!-- Obvious comments add no value --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Attributes Order</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Consistent attribute order:
   1. class
   2. id
   3. data-*
   4. src, href, for, type
   5. title, alt
   6. aria-*, role
--&gt;

<!-- Good: Consistent order --&gt;
<img class="avatar" 
     id="user-avatar" 
     src="avatar.jpg" 
     alt="User avatar" 
     aria-label="User profile picture">

<a class="button" 
   href="/contact" 
   title="Contact us" 
   aria-label="Navigate to contact page">
    Contact
</a>

<input class="form-input" 
       type="email" 
       id="email" 
       name="email" 
       required
       aria-describedby="email-help">`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Boolean Attributes</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Boolean attributes: presence = true --&gt;

<!-- Preferred: No value --&gt;
<input type="checkbox" checked>
<input type="text" required>
<input type="text" disabled>
<button type="submit" autofocus>

<!-- Also valid: Empty string or attribute name --&gt;
<input type="checkbox" checked="">
<input type="text" required="required">

<!--  Wrong: Don't use "true" --&gt;
<input type="checkbox" checked="true">
<input type="text" required="true">`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Quotes</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Good: Always use quotes --&gt;
<img src="image.jpg" alt="Description">
<div class="container" data-value="123">

<!--  Bad: No quotes (works but not recommended) --&gt;
<img src=image.jpg alt=Description>
<div class=container data-value=123>

<!-- Use double quotes consistently --&gt;
<a href="page.html" title="Page title"></a>

<!-- Or single quotes (but be consistent) --&gt;
<a href='page.html' title='Page title'></a>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Self-Closing Tags</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- HTML5: Self-closing slash is optional --&gt;

<!-- Both are valid --&gt;
<img src="image.jpg" alt="Image">
<img src="image.jpg" alt="Image" />

<br>
<br />

<meta charset="UTF-8">
<meta charset="UTF-8" />

<!-- Be consistent throughout your project --&gt;

<!-- These elements are void (can't have content): --&gt;
<!-- area, base, br, col, embed, hr, img, input, 
     link, meta, param, source, track, wbr --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Accessibility Best Practices</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Always include alt text for images --&gt;
<img src="photo.jpg" alt="Person hiking in mountains">

<!-- Use semantic HTML --&gt;
<button>Click me</button>  <!-- Good --&gt;
<div onclick="...">Click me</div>  <!--  Bad --&gt;

<!-- Label form inputs --&gt;
<label for="email">Email:</label>
<input type="email" id="email" name="email">

<!-- Use proper heading hierarchy --&gt;
<h1>Main Title</h1>
<h2>Section</h2>
<h3>Subsection</h3>

<!-- Add ARIA when needed --&gt;
<button aria-label="Close dialog" onclick="closeDialog()">
    
</button>

<!-- Use landmarks --&gt;
<header role="banner">
<nav role="navigation">
<main role="main">
<footer role="contentinfo">`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Performance Best Practices</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <!-- CSS in head --&gt;
    <link rel="stylesheet" href="styles.css">
    
    <!-- Preload critical resources --&gt;
    <link rel="preload" href="hero-image.jpg" as="image">
    
    <!-- Defer non-critical CSS --&gt;
    <link rel="stylesheet" href="optional.css" media="print" 
          onload="this.media='all'">
</head>
<body>
    <!-- Content --&gt;
    
    <!-- JavaScript at end of body --&gt;
    <script src="script.js" defer></script>
    
    <!-- Or use async for independent scripts --&gt;
    <script src="analytics.js" async></script>
    
    <!-- Lazy load images --&gt;
    <img src="image.jpg" loading="lazy" alt="Description">
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Code Organization</h2>

                <h3>Well-Organized</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <!-- Meta tags --&gt;
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="description" content="Description">
    
    <!-- Title --&gt;
    <title>Page Title</title>
    
    <!-- Stylesheets --&gt;
    <link rel="stylesheet" href="styles.css">
    
    <!-- Favicon --&gt;
    <link rel="icon" href="/favicon.ico">
</head>
<body>
    <!-- Header --&gt;
    <header>
        <!-- Logo --&gt;
        <!-- Navigation --&gt;
    </header>
    
    <!-- Main Content --&gt;
    <main>
        <!-- Hero section --&gt;
        <section class="hero">
            <!-- ... --&gt;
        </section>
        
        <!-- Features section --&gt;
        <section class="features">
            <!-- ... --&gt;
        </section>
    </main>
    
    <!-- Footer --&gt;
    <footer>
        <!-- ... --&gt;
    </footer>
    
    <!-- Scripts --&gt;
    <script src="script.js"></script>
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Common Code Quality Issues</h2>

                <div className="issues-list">
                    <h3> Issue 1: Inline Styles</h3>
                    <div className="code-example error">
                        <CodeBlock language="html" code={`<div style="color: red; font-size: 20px;">Text</div>`} />
                    </div>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<!-- Use CSS classes --&gt;
<div class="alert-text">Text</div>

<style>
.alert-text { color: red; font-size: 20px; }
</style>`} />
                    </div>

                    <h3> Issue 2: Deprecated Tags</h3>
                    <div className="code-example error">
                        <CodeBlock language="html" code={`<center>Centered text</center>
<font color="red">Red text</font>
<marquee>Scrolling text</marquee>`} />
                    </div>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<!-- Use CSS --&gt;
<div style="text-align: center;">Centered text</div>
<span style="color: red;">Red text</span>
<!-- marquee: use CSS animations --&gt;`} />
                    </div>

                    <h3> Issue 3: Divitis</h3>
                    <div className="code-example error">
                        <CodeBlock language="html" code={`<div class="article">
    <div class="title">Title</div>
    <div class="content">Content</div>
</div>`} />
                    </div>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<!-- Use semantic tags --&gt;
<article>
    <h2>Title</h2>
    <p>Content</p>
</article>`} />
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Code Quality Checklist</h2>

                <div className="checklist">
                    <h3>Structure</h3>
                    <ul>
                        <li> Valid DOCTYPE declaration</li>
                        <li> lang attribute on html tag</li>
                        <li> Charset in first 1024 bytes</li>
                        <li> Proper document structure</li>
                        <li> Semantic HTML elements</li>
                    </ul>

                    <h3>Formatting</h3>
                    <ul>
                        <li> Consistent indentation (2 or 4 spaces)</li>
                        <li> One statement per line</li>
                        <li> Attributes in consistent order</li>
                        <li> Always use quotes on attributes</li>
                        <li> Lowercase tag and attribute names</li>
                    </ul>

                    <h3>Best Practices</h3>
                    <ul>
                        <li> No inline styles or scripts</li>
                        <li> Descriptive class/ID names</li>
                        <li> Alt text on images</li>
                        <li> Labels on form inputs</li>
                        <li> Proper heading hierarchy</li>
                        <li> Comments for complex sections</li>
                    </ul>

                    <h3>Performance</h3>
                    <ul>
                        <li> CSS in head</li>
                        <li> JavaScript before closing body tag</li>
                        <li> Lazy loading for images</li>
                        <li> Minify HTML for production</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>21. The Cost of Maintenance: Technical Debt vs. Quality</h2>
                <p>
                    In a fast-moving engineering organization, there is a constant tension
                    between "Shipping Fast" and "Shipping Quality." Poor HTML is
                    often the first symptom of <strong>Technical Debt</strong>.
                    When a developer uses a quick hack (like <code>!important</code> in
                    inline styles) instead of a proper CSS class, they are essentially
                    taking out a "loan" that must be paid back with interest during
                    future maintenance.
                </p>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>Bit Rot</h3>
                        <p>Even perfectly written HTML can suffer from "Bit Rot" if not
                            audited regularly. Obsolete schema markup or deprecated
                            third-party script integrations can slow down your site
                            and introduce security vulnerabilities.</p>
                    </div>
                    <div className="concept-card">
                        <h3>Code Reviews</h3>
                        <p>Quality HTML is a team effort. A robust <strong>PR Check</strong>
                            should look for semantic violations and accessibility
                            regressions as stringently as it looks for logic bugs
                            in the application code.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>22. Enterprise-Scale Pattern Management</h2>
                <p>
                    For sites with thousands of pages, manual HTML editing is impossible.
                    Organizations use <strong>Design Systems</strong> and
                    <strong>Component Libraries</strong> (like Storybook) to enforce
                    quality at scale. Instead of writing raw tags, developers use
                    pre-vetted components that have been audited for performance and
                    accessibility.
                </p>
                <ProTip><p><strong>Senior Architecture Tip:</strong> Use <strong>Slots</strong>
                        and <strong>Composition</strong> in your component architecture.
                        This allows you to maintain the rigid HTML structure required for
                        quality while giving developers the flexibility to inject
                        different types of content into specific "safe" areas.</p></ProTip>
                <div className="code-example">
                    <div className="code-header">
                        <span>Pattern Consistency Example</span>
                    </div>
                    <CodeBlock language="html" code={`<!--  Bad: Inconsistent patterns across different pages --&gt;
<div class="user-item">...</div>
<section class="account-block">...</div>

<!-- Good: Use a standardized patterns --&gt;
<ff-profile-card type="user">...</ff-profile-card>
<ff-profile-card type="account">...</ff-profile-card>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>23. Automated Quality Enforcements: CI/CD Workflows</h2>
                <p>
                    In large engineering teams, quality is not left to chance or manual
                    checklists. It is enforced through <strong>Automated Workflows</strong>
                    that run during the CI/CD pipeline.
                </p>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>Static Analysis (Linting)</h3>
                        <p>Tools like <code>HTMLHint</code> or <code>ESLint</code> with
                            HTML plugins can catch missing alt tags, duplicate IDs, and
                            deprecated elements before they are even committed to
                            version control. This ensures a consistent baseline of
                            quality across thousands of files.</p>
                    </div>
                    <div className="concept-card">
                        <h3>Lighthouse CI</h3>
                        <p>Integrating <strong>Lighthouse</strong> into your deployment
                            pipeline allows you to set "Performance Budgets." If a code
                            change causes the accessibility score to drop below 90 or
                            the DOM size to exceed limits, the build is automatically
                            rejected.</p>
                    </div>
                </div>
                <ProTip><p><strong>DevOps Strategy:</strong> Use <strong>Pre-Commit Hooks</strong>
                        (via <code>Husky</code>) to run formatters like <code>Prettier</code>
                        on every save. This removes "bikeshedding" about indentation or
                        quote styles from code reviews, letting the team focus on
                        logic and accessibility.</p></ProTip>
            </div >

            <div className="content-section">
                <h2>24. Theoretical Deep Dive: The Document Object Model (DOM) Lifecycle</h2>
                <p>
                    Understanding how quality HTML affects the browser's internal systems
                    requires a look at the <strong>DOM Lifecycle</strong>. When the
                    browser downloads a high-quality HTML file, it performs the
                    following steps in a highly optimized sequence:
                </p>
                <ul>
                    <li><strong>Tokenization:</strong> Breaking the raw string into start tags, end tags, and content.</li>
                    <li><strong>Tree Construction:</strong> Building the parent-child relationships that form the DOM tree.</li>
                    <li><strong>Attachment:</strong> Calculating the styling for each node by matching it against the CSSOM (CSS Object Model).</li>
                    <li><strong>Layout (Reflow):</strong> Determining the exact geometry (width, height, position) of every element.</li>
                    <li><strong>Painting:</strong> Filling in the pixels on the screen.</li>
                </ul>
                <p>
                    Invalid or inefficient HTML (like using <code>&lt;table&gt;</code> for layout)
                    forces the browser to stay in the <strong>Layout</strong> phase significantly
                    longer, as every coordinate change can potentially affect the entire page.
                    Simplified, semantic HTML allows the browser to "Short-Circuit"
                    re-paints, leading to a much smoother user experience on low-powered
                    mobile devices.
                </p>
            </div>

            <div className="content-section">
                <h2>25. Quality Auditing Tools</h2>
                <h3>Linters & Validators</h3>
                <ul>
                    <li><strong>W3C Validator:</strong> https://validator.w3.org/</li>
                    <li><strong>HTMLHint:</strong> Static code analysis for HTML</li>
                    <li><strong>Prettier:</strong> Code formatter</li>
                    <li><strong>EditorConfig:</strong> Maintain consistent coding styles</li>
                </ul>

                <h3>Browser DevTools</h3>
                <ul>
                    <li>Chrome Lighthouse (audit tool)</li>
                    <li>HTML validation in DevTools</li>
                    <li>Accessibility insights</li>
                </ul>
            </div>

        </div >
    );
};

export default HTMLCodeQuality;



